import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import CurrencyFormat from 'src/components/currency/Currency';
import ProductAvailabilityLabel from 'src/components/productAvailabilityLabel';
import RootStore from 'src/stores/RootStore';
import { TProductModel } from 'src/stores/types/ProductTypes';
import { DEFAULT_BREKR_BATTERY_CODES } from 'src/utils/constants';

interface SelectBrekrBatteryProps {
    store?: typeof RootStore.Type;
    selectedBrekrBatteryCode: string;
    setSelectedBrekrBatteryCode: (selected: string) => void;
}

const SelectBrekrBattery = inject('store')(
    observer(({ store, selectedBrekrBatteryCode, setSelectedBrekrBatteryCode }: SelectBrekrBatteryProps) => {
        const brekrBatteryList: TProductModel[] | [] = store?.products.brekrBatteryList;
        const [isOpen, setOpen] = useState(false);

        const hideBatteryPriceDifference: boolean = store!.config.isConsumer;

        useEffect(() => {
            if (brekrBatteryList?.length) {
                const defaultValue = brekrBatteryList.find(
                    part =>
                        (DEFAULT_BREKR_BATTERY_CODES.has(part.id) && part.productInformation.inStock) ??
                        part.productInformation.inStock,
                );
                if (defaultValue) {
                    setSelectedBrekrBatteryCode(defaultValue.id);
                } else {
                    setSelectedBrekrBatteryCode('empty');
                }
            }
        }, [brekrBatteryList]);

        if (!brekrBatteryList?.length) {
            return null;
        }

        const getOption = (value: string) => {
            const foundOption = brekrBatteryList.find(part => part.id === value);
            return foundOption ? foundOption.productInformation.name : <></>;
        };

        return (
            <FormControl variant="standard" sx={{ margin: '10px 0 10px', width: '100%' }}>
                <InputLabel htmlFor="brekr-battery-item">
                    <FormattedMessage id="select.battery" />
                </InputLabel>
                <Select
                    data-testid="BrekrBatterysSelector"
                    labelId="brekr-battery-item"
                    open={isOpen}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    value={selectedBrekrBatteryCode}
                    name="selectedBrekrBatterysColor"
                    displayEmpty={false}
                    renderValue={(value: any) => getOption(value)}
                    onChange={e => setSelectedBrekrBatteryCode(e.target.value as string)}
                    sx={{ overflow: 'hidden' }}
                >
                    {brekrBatteryList.map((part: TProductModel) => (
                        <MenuItem
                            key={part.id}
                            value={part.id}
                            data-testid={`brekr-battery-${part.id}`}
                            style={{ display: 'flex', justifyContent: 'space-between' }}
                            disabled={!part.productInformation.inStock && !part.productInformation.availableFrom}
                        >
                            <span>
                                <span>{part.productInformation.name}</span>
                                {isOpen && !hideBatteryPriceDifference ? (
                                    <span>
                                        <span>{' (+'}</span>

                                        <span>
                                            <CurrencyFormat value={part.productInformation.unitPrice ?? 0} />
                                        </span>

                                        <span>{')'}</span>
                                    </span>
                                ) : null}
                            </span>
                            <ProductAvailabilityLabel
                                inStock={part.productInformation.inStock}
                                availableFrom={part.productInformation.availableFrom}
                                stockQuantity={part.productInformation.stockQuantity}
                            />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }),
);

export default SelectBrekrBattery;
